import axiosInstance from '../utils/AxiosConfig';

export const getReceivedInvoices = async ({ year, month, page = 0, pageSize = 10, search = '', rut }) => {
  try {
    const response = await axiosInstance.get(`/invoice/received`, {
      params: { year, month, page, pageSize, search, rut },
    });

    // El backend puede devolver { data: { invoices, totalCount } } o directamente { invoices, totalCount }
    const payload = response.data;
    const data = payload?.data ?? payload;

    // Verificar que tenga la estructura esperada
    if (data && typeof data === 'object' && Array.isArray(data.invoices)) {
      const invoices = data.invoices;
      const totalFromApi = typeof data.totalCount === 'number' ? data.totalCount : null;

      const totalCount =
        totalFromApi != null
          ? totalFromApi
          : invoices.length === pageSize
            ? (page + 1) * pageSize + 1
            : page * pageSize + invoices.length;

      return {
        invoices,
        totalCount,
      };
    }

    // Si no tiene la estructura esperada, retornar estructura por defecto
    return { invoices: [], totalCount: 0 };
  } catch (error) {
    console.error('Error fetching received invoices:', error);
    return { invoices: [], totalCount: 0 };
  }
};

export const getReceivedInvoiceItemMappings = async ({ supplierRut, items = [], itemKeys = [] }) => {
  const response = await axiosInstance.post('/invoice/received/item-mappings', {
    supplier_rut: supplierRut,
    items: items.map((item) => ({
      external_item_id: item?.externalItemId,
      description: item?.description,
    })),
    item_keys: itemKeys,
  }, { skipGlobalErrorRedirect: true });
  return response.data?.data ?? response.data ?? [];
};

// Distinct supplier lines from recent received invoices that still have no link.
export const getUnlinkedReceivedInvoiceItems = async (supplierRut) => {
  const response = await axiosInstance.post('/invoice/received/unlinked-items', {
    supplier_rut: supplierRut,
  }, { skipGlobalErrorRedirect: true });
  return response.data?.data ?? response.data ?? [];
};

// Link only: never creates a replenishment. Pending replenishments are generated by the backend
// from later invoices that contain linked items.
export const saveReceivedInvoiceItemLinks = async (payload) => {
  const response = await axiosInstance.post('/invoice/received/item-mappings/save', payload, { skipGlobalErrorRedirect: true });
  return response.data?.data ?? response.data;
};

export const getSummaryInvoices = async ({ year, month, rut }) => {
  try {
    const response = await axiosInstance.get(`/tax/summary`, {
      params: { year, month, rut },
    });
    const payload = response.data;
    const data = payload?.data ?? payload;
    return data ?? null;
  } catch (error) {
    console.error('Error fetching tax summary:', error);
    throw error;
  }
};

export const recalculateTaxSummary = async ({ year, month, rut }) => {
  const response = await axiosInstance.post(`/tax/summary/recalculate`, { year, month, rut });
  const payload = response.data;
  return payload?.data ?? payload;
};

/** Cierra el mes fiscal (marca IVA realizado). POST /tax/summary/close */
export const closeTaxSummaryMonth = async ({ year, month, rut }) => {
  const response = await axiosInstance.post(`/tax/summary/close`, { year, month, rut });
  const payload = response.data;
  return payload?.data ?? payload;
};

/** Ajusta manualmente los valores del resumen fiscal y recalcula IVA a pagar/saldo. */
export const adjustTaxSummary = async ({
  year,
  month,
  ivaVenta,
  ivaCompra,
  saldoMesAnterior,
  resguardo = 0,
  cantidadEmitidas,
  cantidadRecibidas,
  rut,
}) => {
  const response = await axiosInstance.put(`/tax/summary/manual-adjust`, {
    year,
    month,
    rut,
    ivaVenta,
    ivaCompra,
    saldoMesAnterior,
    resguardo,
    cantidadEmitidas,
    cantidadRecibidas,
  });
  const payload = response.data;
  return payload?.data ?? payload;
};

/** Lista de meses cerrados fiscalmente { year, month }[] para deshabilitar en el date picker. */
export const getClosedMonths = async ({ fromYear, toYear }) => {
  const response = await axiosInstance.get(`/tax/summary/closed-months`, {
    params: { fromYear, toYear },
  });
  const payload = response.data;
  const data = payload?.data ?? payload;
  return Array.isArray(data) ? data : [];
};

export const getConfiguredBillingRuts = async ({ allLocations = false } = {}) => {
  // Sin allLocations la llamada es idéntica a la de siempre (una entrada por RUT).
  const response = allLocations
    ? await axiosInstance.get('/settings/billing-ruts', { params: { allLocations: true } })
    : await axiosInstance.get('/settings/billing-ruts');
  // El backend puede devolver directamente el array o dentro de response.data.data.
  const ruts = response.data?.data ?? response.data ?? [];
  return Array.isArray(ruts) ? ruts : [];
};
